(() => {
  'use strict';

  const SEARCH_FORM_SELECTOR = 'form[action*="/search"]';

  /*
   * Every search word can exist in any of these fields.
   *
   * Example: "mkv 508"
   * - mkv can match the vendor
   * - 508 can match the SKU
   *
   * All words are still required because the groups
   * are connected using AND.
   */
  const SEARCH_FIELDS = [
    'title',
    'variants.title',
    'vendor'
    ];

  /**
   * Removes extra spaces.
   */
  function normalizeCustomerQuery(value) {
    return String(value || '')
      .trim()
      .replace(/\s+/g, ' ');
  }

  /**
   * Escapes characters that have special meaning
   * in Shopify search syntax.
   */
  function escapeSearchValue(value) {
    return value.replace(/([:\\()"])/g, '\\$1');
  }

  /**
   * Converts escaped values back to normal text.
   */
  function unescapeSearchValue(value) {
    return value.replace(/\\([:\\()"])/g, '$1');
  }

  /**
   * Converts:
   *
   * mkv 508
   *
   * Into:
   *
   * (
   *   title:"mkv"
   *   OR variants.title:"mkv"
   *   OR vendor:"mkv"
   *   OR product_type:"mkv"
   *   OR tag:"mkv"
   *   OR variants.sku:"mkv"
   *   OR variants.barcode:"mkv"
   * )
   * AND
   * (
   *   title:"508"
   *   OR variants.title:"508"
   *   OR vendor:"508"
   *   OR product_type:"508"
   *   OR tag:"508"
   *   OR variants.sku:"508"
   *   OR variants.barcode:"508"
   * )
   */
  function buildStrictProductQuery(value) {
    const normalizedQuery = normalizeCustomerQuery(value);

    if (!normalizedQuery) {
      return '';
    }

    return normalizedQuery
      .split(' ')
      .filter(Boolean)
      .map((term) => {
        const escapedTerm = escapeSearchValue(term);

        const fieldConditions = SEARCH_FIELDS.map(
          (field) => `${field}:"${escapedTerm}"`
        );

        return `(${fieldConditions.join(' OR ')})`;
      })
      .join(' AND ');
  }

  /**
   * Converts the internal Shopify query back into
   * customer-facing text.
   *
   * The first field in each generated group is always title,
   * so its value contains the original customer term.
   */
  function extractCustomerQuery(query) {
    if (!query || typeof query !== 'string') {
      return null;
    }

    if (!query.includes('(title:"')) {
      return null;
    }

    const groups = query.trim().split(/\s+AND\s+/i);

    if (!groups.length) {
      return null;
    }

    const terms = [];

    for (const group of groups) {
      const match = group.match(
        /^\(\s*title:"((?:\\.|[^"])*)"/i
      );

      if (!match) {
        return null;
      }

      terms.push(unescapeSearchValue(match[1]));
    }

    return terms.join(' ');
  }

  /**
   * Gets the search input from a form.
   */
  function getSearchInput(form) {
    return form.querySelector('input[name="q"]');
  }

  /**
   * Creates the strict search URL without changing
   * what the customer sees inside the search field.
   */
  function buildSearchUrl(form, strictQuery) {
    const formAction =
      form.getAttribute('action') || '/search';

    const searchUrl = new URL(
      formAction,
      window.location.origin
    );

    searchUrl.search = '';

    const formData = new FormData(form);

    for (const [name, value] of formData.entries()) {
      /*
       * Add the strict q value separately.
       * Exclude page so new searches begin on page one.
       */
      if (name === 'q' || name === 'page') {
        continue;
      }

      if (typeof value !== 'string') {
        continue;
      }

      searchUrl.searchParams.append(name, value);
    }

    searchUrl.searchParams.set('q', strictQuery);
    searchUrl.searchParams.set('type', 'product');

    /*
     * Prevent partial matching on the last word.
     */
    searchUrl.searchParams.set(
      'options[prefix]',
      'none'
    );

    return searchUrl;
  }

  /**
   * Handles search form submission.
   */
  function handleSearchSubmit(event) {
    const form = event.target;

    if (!(form instanceof HTMLFormElement)) {
      return;
    }

    if (!form.matches(SEARCH_FORM_SELECTOR)) {
      return;
    }

    const searchInput = getSearchInput(form);

    if (!searchInput) {
      return;
    }

    let customerQuery = normalizeCustomerQuery(
      searchInput.value
    );

    if (!customerQuery) {
      return;
    }

    /*
     * Prevent transforming an internal query twice.
     */
    const extractedQuery =
      extractCustomerQuery(customerQuery);

    if (extractedQuery) {
      customerQuery = extractedQuery;
    }

    const strictQuery =
      buildStrictProductQuery(customerQuery);

    if (!strictQuery) {
      return;
    }

    const searchUrl = buildSearchUrl(
      form,
      strictQuery
    );

    event.preventDefault();
    event.stopImmediatePropagation();

    window.location.assign(searchUrl.toString());
  }

  /**
   * Restores normal customer text in all search fields.
   */
  function restoreCustomerFacingInputs() {
    const urlParameters = new URLSearchParams(
      window.location.search
    );

    const internalQuery = urlParameters.get('q');

    if (!internalQuery) {
      return;
    }

    const customerQuery =
      extractCustomerQuery(internalQuery);

    if (!customerQuery) {
      return;
    }

    document
      .querySelectorAll(SEARCH_FORM_SELECTOR)
      .forEach((form) => {
        const searchInput = getSearchInput(form);

        if (searchInput) {
          searchInput.value = customerQuery;
        }
      });
  }

  document.addEventListener(
    'submit',
    handleSearchSubmit,
    true
  );

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      restoreCustomerFacingInputs
    );
  } else {
    restoreCustomerFacingInputs();
  }

  /*
   * Restore fields when navigating using browser history.
   */
  window.addEventListener(
    'pageshow',
    restoreCustomerFacingInputs
  );

  /*
   * Restore dynamically inserted header or drawer searches.
   */
  document.addEventListener('focusin', (event) => {
    const input = event.target;

    if (!(input instanceof HTMLInputElement)) {
      return;
    }

    if (input.name !== 'q') {
      return;
    }

    const customerQuery =
      extractCustomerQuery(input.value);

    if (customerQuery) {
      input.value = customerQuery;
    }
  });
})();